import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, GripVertical } from 'lucide-react';

interface Column {
  key: string;
  header: string;
  render?: (value: any, row: any) => React.ReactNode;
}

interface PaginationInfo {
  page: number;
  page_size: number;
  total: number;
}

interface TableProps {
  columns: Column[];
  data: any[];
  isLoading?: boolean;
  emptyMessage?: string;
  pagination?: PaginationInfo;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  rowClassName?: string;
  cellClassName?: string;
  /** When set, rows get a drag handle and can be reordered (index-based, like MenuEditor). */
  onRowDrop?: (fromIndex: number, toIndex: number) => void;
}
export const Table: React.FC<TableProps> = ({
  columns,
  data,
  isLoading = false,
  emptyMessage = 'No data available',
  pagination,
  onPageChange,
  onPageSizeChange,
  rowClassName = '',
  cellClassName = '',
  onRowDrop
}) => {
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const draggable = !!onRowDrop;

  const handleDrop = (dropIndex: number) => {
    const from = dragIndex;
    setDragIndex(null);
    setDragOverIndex(null);
    if (from === null || from === dropIndex || !onRowDrop) return;
    onRowDrop(from, dropIndex);
  };
  if (isLoading) {
    return <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
        </div>
      </div>;
  }

  const totalPages = pagination ? Math.ceil(pagination.total / pagination.page_size) : 0;
  const startItem = pagination ? (pagination.page - 1) * pagination.page_size + 1 : 0;
  const endItem = pagination ? Math.min(pagination.page * pagination.page_size, pagination.total) : 0;

  const handlePageChange = (newPage: number) => {
    if (onPageChange && newPage >= 1 && newPage <= totalPages) {
      onPageChange(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  return <div className="bg-white rounded-lg shadow overflow-hidden">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {draggable && <th scope="col" className="w-8 pl-4 pr-0 py-3" />}
              {columns.map(column => <th key={column.key} scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  {column.header}
                </th>)}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200 align-top">
            {data.length > 0 ? data.map((row, rowIndex) => <tr
                  key={rowIndex}
                  draggable={draggable}
                  onDragStart={draggable ? () => setDragIndex(rowIndex) : undefined}
                  onDragEnd={draggable ? () => { setDragIndex(null); setDragOverIndex(null); } : undefined}
                  onDragOver={draggable ? (e) => { e.preventDefault(); if (dragIndex !== null && dragIndex !== rowIndex) setDragOverIndex(rowIndex); } : undefined}
                  onDrop={draggable ? (e) => { e.preventDefault(); handleDrop(rowIndex); } : undefined}
                  className={`hover:bg-gray-50 align-top ${dragOverIndex === rowIndex ? 'bg-indigo-50' : ''} ${rowClassName}`}
                >
                  {draggable && <td className="w-8 pl-4 pr-0 py-4 align-top">
                      <GripVertical className="h-4 w-4 text-gray-300 cursor-grab" />
                    </td>}
                  {columns.map(column => <td key={`${rowIndex}-${column.key}`} className={`px-6 py-4 whitespace-nowrap text-sm text-gray-500 align-top ${cellClassName}`}>
                      {column.render ? column.render(row[column.key], row) : row[column.key]}
                    </td>)}
                </tr>) : <tr>
                <td colSpan={columns.length + (draggable ? 1 : 0)} className="px-6 py-12 text-center text-sm text-gray-500">
                  {emptyMessage}
                </td>
              </tr>}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      {pagination && totalPages > 1 && (
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => handlePageChange(pagination.page - 1)}
              disabled={pagination.page <= 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.page + 1)}
              disabled={pagination.page >= totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of{' '}
                <span className="font-medium">{pagination.total}</span> results
              </p>
            </div>
            <div className="flex items-center space-x-2">
              <div className="flex items-center">
                <label htmlFor="page-size" className="text-sm text-gray-700 mr-2">
                  Show:
                </label>
                <select
                  id="page-size"
                  value={pagination.page_size}
                  onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                  className="block w-20 px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="flex items-center space-x-1">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (pagination.page <= 3) {
                    pageNum = i + 1;
                  } else if (pagination.page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = pagination.page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 text-sm font-medium ${
                        pageNum === pagination.page
                          ? 'z-10 bg-indigo-50 border-indigo-500 text-indigo-600'
                          : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                      } border rounded-md`}
                    >
                      {pageNum}
                    </button>
                  );
                })}

                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= totalPages}
                  className="relative inline-flex items-center px-2 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-500 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>;
};
